import { useRef, useCallback, useState, useEffect } from "react";
import likapaiLogo from "./likapai-logo.png";
import likapaiLogoFull from "./likapai-logo-full.png";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Dashboard from "./Dashboard";
import Admin from "./Admin";
import SessionView from "./SessionView";
import TablesTab from "./TablesTab";
import OrderCard from "./OrderCard";
import DoneCard from "./DoneCard";
import ManualOrderModal from "./ManualOrderModal";
import { playNewOrderSound, playAlarmSound } from "./utils";
import { AuthProvider, useAuth } from "./AuthContext";
import ProtectedRoute from "./ProtectedRoute";
import LoginPage from "./LoginPage";
import { IcFire, IcChair, IcCheck, IcChart, IcSettings, IcBell, IcLock } from "./Icons";
import ChatWidget from "./ChatWidget";
import { pushSupported, isStandalone, registerServiceWorker, getPushState, enablePush, disablePush } from "./push";

// ── Sipariş Alarm Kartı (sağ üst, toast stili) ────────────────────────────────
function OrderAlarmCard({ orders, onDismiss }) {
  const [expanded, setExpanded] = useState(false);
  if (!orders.length) return null;

  const tableLabels = [...new Set(orders.map((o) => o.table_label).filter(Boolean))];
  // Tüm siparişlerde gerçek ürün kalemi var mı?
  const hasItems = orders.some((o) => o.items && o.items.length > 0);
  const multiOrder = orders.length > 1;

  return (
    <div className="alarm-card">
      {/* Kompakt header — her zaman görünür */}
      <div
        className={`alarm-card-header${hasItems ? " alarm-card-header--clickable" : ""}`}
        onClick={hasItems ? () => setExpanded((e) => !e) : undefined}
        role={hasItems ? "button" : undefined}
        aria-expanded={hasItems ? expanded : undefined}
      >
        <span className="alarm-card-bell"><IcBell size={20} /></span>
        <div className="alarm-card-info">
          <div className="alarm-card-title">Yeni Sipariş Geldi</div>
          <div className="alarm-card-sub">
            {orders.length} sipariş
            {tableLabels.length > 0 && ` — ${tableLabels.join(", ")}`}
          </div>
        </div>
        {hasItems && (
          <span className="alarm-card-chevron">{expanded ? "▲" : "▼"}</span>
        )}
      </div>

      {/* Ürün listesi — sadece items varsa ve genişletilmişse */}
      {hasItems && expanded && (
        <div className="alarm-card-detail">
          {orders.map((o) => (
            <div key={o.id} className="alarm-card-order-block">
              {multiOrder && o.table_label && (
                <div className="alarm-card-order-table">{o.table_label}</div>
              )}
              {(o.items || []).map((item, idx) => (
                <div key={idx} className="alarm-card-item">
                  <span className="alarm-card-item-qty">{item.quantity}×</span>
                  <span className="alarm-card-item-name">{item.product_name}</span>
                  {item.item_note && (
                    <span className="alarm-card-item-note">{item.item_note}</span>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {/* Tek aksiyon */}
      <div className="alarm-card-actions">
        <button className="alarm-card-dismiss-btn" onClick={onDismiss}>
          Tamam, alarmı kapat
        </button>
      </div>
    </div>
  );
}

// ── PIN Kapısı Modalı ──────────────────────────────────────────────────────────
function PinGateModal({ onSuccess, onClose }) {
  const [pin, setPin]   = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  // Autofill bazen React state'i güncellemez — DOM'dan okuyarak fallback
  const resolvePin = () => {
    if (pin.length >= 4) return pin;
    const domVal = (inputRef.current?.value || "").replace(/\D/g, "").slice(0, 4);
    if (domVal.length >= 4) setPin(domVal);
    return domVal;
  };

  const submit = async (pinValue) => {
    const val = pinValue ?? resolvePin();
    if (val.length < 4 || busy) return;
    setBusy(true);
    setError("");
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL ?? ""}/api/admin/pin-verify`, {
        method:      "POST",
        credentials: "include",
        headers:     { "Content-Type": "application/json" },
        body:        JSON.stringify({ pin: val }),
      });
      if (res.ok) {
        onSuccess();
      } else if (res.status === 429) {
        const d = await res.json().catch(() => ({}));
        setError(d.detail || "Çok fazla hatalı deneme. Bekleyin.");
      } else {
        setError("PIN hatalı");
        setPin("");
      }
    } catch {
      setError("Bağlantı hatası");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={() => !busy && onClose()}>
      <div className="pin-gate-card" onClick={(e) => e.stopPropagation()}>
        <div className="pin-gate-icon"><IcLock size={44} /></div>
        <div className="pin-gate-title">PIN Gerekli</div>
        <div className="pin-gate-sub">Devam etmek için yönetici PIN'ini girin</div>
        <input
          ref={inputRef}
          className="pin-gate-input"
          type="password"
          inputMode="numeric"
          maxLength={4}
          value={pin}
          autoFocus
          placeholder="••••"
          onChange={(e) => {
            const val = e.target.value.replace(/\D/g, "").slice(0, 4);
            setPin(val);
            if (val.length === 4) setTimeout(() => submit(val), 80);
          }}
          onKeyDown={(e) => e.key === "Enter" && submit()}
        />
        {error && <div className="pin-gate-error">{error}</div>}
        <div className="pin-gate-actions">
          <button className="btn-secondary" disabled={busy} onClick={onClose}>İptal</button>
          <button className="btn-save" disabled={busy} onClick={() => submit()}>
            {busy ? "…" : "Giriş"}
          </button>
        </div>
      </div>
    </div>
  );
}

// "" → relative URL: dev'de CRA proxy (package.json "proxy"), prod'da Nginx same-origin
const API = process.env.REACT_APP_API_URL ?? "";
const POLL_INTERVAL = 4000; // ms

// ── Ana Uygulama Kabuğu ────────────────────────────────────────────────────────
function AppShell() {
  const { user, logout } = useAuth();

  const [activeOrders, setActiveOrders] = useState([]);
  const [doneOrders, setDoneOrders] = useState([]);
  const [tab, setTab] = useState("active");
  const [navOpen, setNavOpen] = useState(false);   // mobil çekmece menü
  useEffect(() => { setNavOpen(false); }, [tab]);  // sekme değişince çekmeceyi kapat

  // ── Web Push: SW kaydı + mevcut abonelik durumu ──
  const [pushState, setPushState] = useState("off"); // off | on | denied | unsupported | busy
  useEffect(() => {
    registerServiceWorker().then(() => getPushState().then(setPushState));
  }, []);
  const togglePush = async () => {
    if (pushState === "busy") return;
    if (!pushSupported()) {
      alert(
        isStandalone()
          ? "Bu cihaz/tarayıcı bildirimi desteklemiyor."
          : "iPhone'da bildirim için: Safari'de Paylaş → \"Ana Ekrana Ekle\" yapıp paneli o simgeden açın, sonra tekrar deneyin."
      );
      return;
    }
    const prev = pushState;
    setPushState("busy");
    try {
      setPushState(prev === "on" ? await disablePush() : await enablePush());
    } catch (e) {
      if (e.message === "denied") {
        setPushState("denied");
        alert("Bildirim izni reddedildi. Cihaz ayarlarından izin vermeniz gerekir.");
      } else {
        setPushState(prev);
        alert("Bildirim ayarı başarısız oldu.");
      }
    }
  };
  const [newIds, setNewIds] = useState(new Set());
  const [connected, setConnected] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [stats, setStats] = useState({ open: 0, preparing: 0, done: 0 });
  const [showManual, setShowManual] = useState(false);
  const [viewingSession, setViewingSession] = useState(null);
  const [orderingEnabled, setOrderingEnabled] = useState(true);
  const [showRevenue, setShowRevenue] = useState(false);
  const [todayRevenue, setTodayRevenue] = useState(0);
  const [tables,   setTables]   = useState([]);
  const [tableSessions, setTableSessions] = useState([]);
  const knownIds = useRef(new Set());
  const firstLoad = useRef(true);

  // ── Masalar verisi — tab değişiminde kaybolmasın ──────────────────────────────
  const loadTables = useCallback(async () => {
    try {
      const sig = () => AbortSignal.timeout(8000);
      const [tablesRes, openRes, partialRes] = await Promise.all([
        fetch(`${API}/api/admin/tables`, { credentials: "include", signal: sig() }),
        fetch(`${API}/api/sessions?status=OPEN`,            { signal: sig() }),
        fetch(`${API}/api/sessions?status=PARTIALLY_PAID`,  { signal: sig() }),
      ]);
      const tablesData  = await tablesRes.json();
      const openData    = await openRes.json();
      const partialData = await partialRes.json();
      setTables((tablesData.tables || []).filter(t => t.is_active === 1));
      setTableSessions(
        [...(openData.sessions || []), ...(partialData.sessions || [])]
          .filter(s => (s.service_type ?? "TABLE") === "TABLE")
      );
    } catch (e) {
      console.error("Tables load failed:", e);
    }
  }, []);

  useEffect(() => {
    loadTables();
    const iv = setInterval(loadTables, 5000);
    return () => clearInterval(iv);
  }, [loadTables]);

  // ── Tema ─────────────────────────────────────────────────────────────────────

  // ── Alarm state + refs ───────────────────────────────────────────────────────
  const [alarmActive, setAlarmActive] = useState(false);
  const [alarmOrders, setAlarmOrders] = useState([]);
  const alarmActiveRef    = useRef(false);
  const alertSettingsRef  = useRef({ sound: "classic_ding", mode: "once" });
  const repeatIntervalRef = useRef(null);

  // ── PIN Screen-Lock ──────────────────────────────────────────────────────────
  // Her erişimde PIN istenir — sessionStorage optimizasyonu yok.
  const [showPinGate, setShowPinGate] = useState(false);
  const [afterPin, setAfterPin]       = useState(null); // callback: () => void

  // requirePin: her çağrıda PIN kapısını açar, callback doğrulama sonrası çalışır.
  const requirePin = useCallback((callback) => {
    setAfterPin(() => callback);
    setShowPinGate(true);
  }, []);

  // ── Alarm yönetimi ───────────────────────────────────────────────────────────
  const triggerAlarm = useCallback((freshOrders) => {
    if (alarmActiveRef.current) {
      // Alarm zaten aktif — id bazlı unique merge, duplicate ekleme
      setAlarmOrders((prev) => {
        const existingIds = new Set(prev.map((o) => o.id));
        const newOnes = freshOrders.filter((o) => !existingIds.has(o.id));
        return newOnes.length ? [...prev, ...newOnes] : prev;
      });
      return;
    }
    const { sound, mode } = alertSettingsRef.current;
    alarmActiveRef.current = true;
    setAlarmActive(true);
    setAlarmOrders(freshOrders);
    playAlarmSound(sound);
    if (mode === "repeat") {
      repeatIntervalRef.current = setInterval(() => playAlarmSound(sound), 3000);
    }
  }, []);

  const dismissAlarm = useCallback(() => {
    if (repeatIntervalRef.current) {
      clearInterval(repeatIntervalRef.current);
      repeatIntervalRef.current = null;
    }
    alarmActiveRef.current = false;
    setAlarmActive(false);
    setAlarmOrders([]);
  }, []);

  const fetchOrders = useCallback(async () => {
    try {
      const sig = () => AbortSignal.timeout(8000);
      const [activeRes, prepRes, doneRes] = await Promise.all([
        fetch(`${API}/api/orders?status=OPEN`,      { signal: sig() }),
        fetch(`${API}/api/orders?status=PREPARING`, { signal: sig() }),
        fetch(`${API}/api/orders?status=DONE&today=true`, { signal: sig() }),
      ]);
      if (!activeRes.ok) {
        if (activeRes.status !== 429) setConnected(false);
        return;
      }
      const [activeJson, prepJson, doneJson] = await Promise.all([
        activeRes.json(),
        prepRes.ok ? prepRes.json() : { orders: [] },
        doneRes.ok ? doneRes.json() : { orders: [] },
      ]);

      const allActive = [
        ...activeJson.orders,
        ...prepJson.orders,
      ].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

      if (!firstLoad.current) {
        const incoming = new Set(allActive.map((o) => o.id));
        const fresh = [...incoming].filter((id) => !knownIds.current.has(id));
        if (fresh.length > 0) {
          triggerAlarm(fresh.map((id) => allActive.find((o) => o.id === id)).filter(Boolean));
          setNewIds((prev) => new Set([...prev, ...fresh]));
          setTimeout(() => {
            setNewIds((prev) => {
              const next = new Set(prev);
              fresh.forEach((id) => next.delete(id));
              return next;
            });
          }, 3000);
        }
      }
      firstLoad.current = false;
      knownIds.current = new Set(allActive.map((o) => o.id));

      setActiveOrders(allActive);
      setDoneOrders(doneJson.orders.slice(0, 50));
      setStats({
        open:      activeJson.orders.length,
        preparing: prepJson.orders.length,
        done:      doneJson.orders.length,
      });
      setConnected(true);
      setLastUpdate(new Date());
    } catch {
      setConnected(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
    const interval = setInterval(fetchOrders, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchOrders]);

  // Bugünkü ciro — TEK kaynak: backend /dashboard/daily total_revenue (Dashboard ile
  // birebir). Sadece admin; barista'da çağrılmaz (403 gürültüsü olmaz). 20sn'de tazelenir.
  useEffect(() => {
    if (user?.role !== "admin") return;
    let alive = true;
    const loadRevenue = async () => {
      try {
        const res = await fetch(`${API}/api/dashboard/daily`, { credentials: "include" });
        if (!res.ok) return;
        const d = await res.json();
        if (alive) setTodayRevenue(d?.summary?.total_revenue || 0);
      } catch {}
    };
    loadRevenue();
    const iv = setInterval(loadRevenue, 20000);
    return () => { alive = false; clearInterval(iv); };
  }, [user]);

  // Alert settings — mount'ta yükle + Admin'den kayıt gelince anında güncelle
  useEffect(() => {
    fetch(`${API}/api/admin/alert-settings`, { credentials: "include" })
      .then((r) => r.json())
      .then((d) => {
        alertSettingsRef.current = {
          sound: d.order_alert_sound || "classic_ding",
          mode:  d.order_alert_mode  || "once",
        };
      })
      .catch(() => {});

    const onSettingsUpdated = (e) => {
      alertSettingsRef.current = {
        sound: e.detail.order_alert_sound || "classic_ding",
        mode:  e.detail.order_alert_mode  || "once",
      };
    };
    window.addEventListener("alert-settings-updated", onSettingsUpdated);
    return () => window.removeEventListener("alert-settings-updated", onSettingsUpdated);
  }, []);

  // Cleanup — unmount'ta repeat interval temizle
  useEffect(() => {
    return () => {
      if (repeatIntervalRef.current) clearInterval(repeatIntervalRef.current);
    };
  }, []);

  useEffect(() => {
    fetch(`${API}/api/admin/settings/ordering-enabled`)
      .then((r) => r.json())
      .then((d) => setOrderingEnabled(d.enabled !== false))
      .catch(() => {});
  }, []);

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await fetch(`${API}/api/orders/${orderId}/status`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ status: newStatus }),
      });
      await fetchOrders();
    } catch {
      alert("Guncelleme basarisiz");
    }
  };

  // Ciro artık 50-limitli done listesinden DEĞİL, backend total_revenue'dan (yukarıdaki
  // effect). Eskiden slice(0,50) yüzünden 50. siparişten sonrası ciroya girmiyordu.
  const totalRevenue = todayRevenue;

  const isAdmin = user?.role === "admin";

  return (
    <div className="app">
      {tab !== "admin" && (
        <div className="mobile-bar">
          <button className="nav-toggle" onClick={() => setNavOpen(true)} aria-label="Menüyü aç">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <span className="mobile-bar-name">{localStorage.getItem("coas_cafe_name") || process.env.REACT_APP_CAFE_NAME || ""}</span>
        </div>
      )}
      <div className={`nav-backdrop${navOpen ? " show" : ""}`} onClick={() => setNavOpen(false)} />
      <aside className={`sidebar${navOpen ? " sidebar--open" : ""}`}>
        <div className="logo">
          <span className="logo-icon"><svg width="27" height="27" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M8 2.5c-.7.9-.7 1.7 0 2.6" opacity=".6"/><path d="M11.5 1.8c-.8 1-.8 2 0 3" opacity=".6"/><path d="M15 2.5c-.7.9-.7 1.7 0 2.6" opacity=".6"/><path d="M4 8h12v4.5a5.5 5.5 0 0 1-5.5 5.5h-1A5.5 5.5 0 0 1 4 12.5V8Z"/><path d="M16 9.2h1.8a2.3 2.3 0 0 1 0 4.6H16"/><path d="M3 21h14"/></svg></span>
          <div>
            <div className="logo-name">{localStorage.getItem("coas_cafe_name") || process.env.REACT_APP_CAFE_NAME || ""}</div>
            <div className="logo-sub">Yönetim Paneli</div>
          </div>
        </div>

        <div className="stat-cards">
          <div className="stat-card stat-open">
            <div className="stat-val">{stats.open}</div>
            <div className="stat-label">Bekleyen</div>
          </div>
          <div className="stat-card stat-prep">
            <div className="stat-val">{stats.preparing}</div>
            <div className="stat-label">Hazırlanıyor</div>
          </div>
          <div className="stat-card stat-done">
            <div className="stat-val">{stats.done}</div>
            <div className="stat-label">Tamamlanan</div>
          </div>
        </div>

        {isAdmin && (
          <div className="revenue-card">
            <div className="rev-label">
              Bugünkü Ciro
              <button
                className={`rev-toggle${showRevenue ? " rev-toggle--on" : ""}`}
                onClick={() => {
                  if (showRevenue) {
                    setShowRevenue(false);
                  } else {
                    requirePin(() => setShowRevenue(true));
                  }
                }}
                aria-label={showRevenue ? "Gizle" : "Goster"}
              >
                <span className="rev-toggle-track">
                  <span className="rev-toggle-knob" />
                </span>
              </button>
            </div>
            {showRevenue
              ? <div className="rev-amount">&#8378;{Math.round(totalRevenue).toLocaleString("tr-TR")}</div>
              : <div className="rev-amount rev-hidden">&#8226;&#8226;&#8226;&#8226;</div>
            }
          </div>
        )}

        <div className="sidebar-nav">
          <button className={`nav-btn${tab === "active" ? " active" : ""}`} onClick={() => setTab("active")}>
            <span className="nav-icon"><IcFire size={20} /></span>
            <span>Aktif Siparişler</span>
            {activeOrders.length > 0 && <span className="nav-badge">{activeOrders.length}</span>}
          </button>
          <button className={`nav-btn${tab === "tables" ? " active" : ""}`} onClick={() => setTab("tables")}>
            <span className="nav-icon"><IcChair size={20} /></span>
            <span>Masalar</span>
          </button>
          <button className={`nav-btn${tab === "done" ? " active" : ""}`} onClick={() => setTab("done")}>
            <span className="nav-icon"><IcCheck size={20} /></span>
            <span>Tamamlananlar</span>
          </button>
          {isAdmin && (
            <button className={`nav-btn${tab === "dashboard" ? " active" : ""}`} onClick={() => requirePin(() => setTab("dashboard"))}>
              <span className="nav-icon"><IcChart size={20} /></span>
              <span>Dashboard</span>
            </button>
          )}
          {isAdmin && (
            <button className={`nav-btn${tab === "admin" ? " active" : ""}`} onClick={() => requirePin(() => setTab("admin"))}>
              <span className="nav-icon"><IcSettings size={20} /></span>
              <span>Yönetici</span>
            </button>
          )}
        </div>

        <button className="manual-order-btn" onClick={() => { setNavOpen(false); setShowManual(true); }}>
          + Manuel Sipariş
        </button>

        <button
          className={`sidebar-notif${pushState === "on" ? " on" : ""}${pushState === "denied" ? " denied" : ""}`}
          onClick={togglePush}
          title="Yeni sipariş bildirimleri"
        >
          <span className="nav-icon"><IcBell size={17} /></span>
          <span>
            {pushState === "on" ? "Bildirimler açık"
              : pushState === "busy" ? "…"
              : pushState === "denied" ? "Bildirim engelli"
              : "Bildirimleri aç"}
          </span>
          <span className={`notif-dot${pushState === "on" ? " on" : ""}`} />
        </button>

        <div className="sidebar-user">
          <div className="sidebar-user-info">
            <span className="sidebar-user-name">{user?.full_name || user?.username}</span>
            <span className="sidebar-user-role">{user?.role}</span>
          </div>
          <button className="sidebar-logout-btn" onClick={logout} title="Çıkış yap"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg></button>
        </div>

        <div className={`connection-status${connected ? " ok" : " err"}`}>
          <span className="conn-dot" />
          {connected
            ? `Canli ${lastUpdate ? lastUpdate.toLocaleTimeString("tr-TR") : ""}`
            : "Baglanti kesildi"}
        </div>
      </aside>

      <main className="panel">
        {tab === "active" ? (
          <>
            <div className="panel-header">
              <h1 className="panel-title">
                {activeOrders.length === 0 ? "Aktif sipariş yok" : `${activeOrders.length} aktif sipariş`}
              </h1>
            </div>
            {activeOrders.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon"><svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M8 2.5c-.7.9-.7 1.7 0 2.6" opacity=".6"/><path d="M11.5 1.8c-.8 1-.8 2 0 3" opacity=".6"/><path d="M15 2.5c-.7.9-.7 1.7 0 2.6" opacity=".6"/><path d="M4 8h12v4.5a5.5 5.5 0 0 1-5.5 5.5h-1A5.5 5.5 0 0 1 4 12.5V8Z"/><path d="M16 9.2h1.8a2.3 2.3 0 0 1 0 4.6H16"/><path d="M3 21h14"/></svg></div>
                <div className="empty-text">Şu an her şey sakin</div>
                <div className="empty-sub">Yeni siparişler burada görünecek</div>
              </div>
            ) : (
              <div className="orders-grid">
                {activeOrders.map((order) => (
                  <OrderCard
                    key={order.id}
                    order={order}
                    onStatusChange={handleStatusChange}
                    onCancel={(id) => requirePin(() => handleStatusChange(id, "CANCELLED"))}
                    isNew={newIds.has(order.id)}
                    isAlarm={alarmOrders.some((ao) => ao.id === order.id)}
                  />
                ))}
              </div>
            )}
          </>
        ) : tab === "done" ? (
          <>
            <div className="panel-header">
              <h1 className="panel-title">{doneOrders.length} tamamlanan siparis</h1>
            </div>
            <div className="done-list">
              {doneOrders.map((order) => <DoneCard key={order.id} order={order} />)}
            </div>
          </>
        ) : tab === "dashboard" && isAdmin ? (
          <Dashboard />
        ) : tab === "tables" ? (
          <TablesTab tables={tables} sessions={tableSessions} onViewSession={setViewingSession} />
        ) : tab === "admin" && isAdmin ? (
          <Admin onBack={() => setTab("active")} />
        ) : null}
      </main>

      {isAdmin && <ChatWidget />}
      {showManual && (
        <ManualOrderModal
          onClose={() => setShowManual(false)}
          onSuccess={(result) => {
            fetchOrders();
            if (result?.takeaway_session_id) {
              setViewingSession(result.takeaway_session_id);
            }
          }}
        />
      )}
      {viewingSession && (
        <SessionView sessionId={viewingSession} onClose={() => { setViewingSession(null); loadTables(); }} onSessionClosed={() => { fetchOrders(); loadTables(); }} onRequirePin={requirePin} />
      )}
      {showPinGate && (
        <PinGateModal
          onSuccess={() => {
            setShowPinGate(false);
            if (afterPin) { afterPin(); setAfterPin(null); }
          }}
          onClose={() => { setShowPinGate(false); setAfterPin(null); }}
        />
      )}
      {alarmActive && (
        <OrderAlarmCard orders={alarmOrders} onDismiss={dismissAlarm} />
      )}
      <img src={likapaiLogo} alt="LikapAI" style={{position:"fixed",bottom:20,right:20,width:128,height:128,objectFit:"contain",opacity:0.85,pointerEvents:"none",zIndex:1}} />
    </div>
  );
}

// ── Router ve Auth Sarmali ─────────────────────────────────────────────────────
export default function App() {
  const [theme, setTheme] = useState(
    () => localStorage.getItem("coas_theme") || "vesaire"
  );
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
  }, [theme]);
  useEffect(() => {
    const onThemeChanged = (e) => {
      const t = e.detail?.theme || "vesaire";
      localStorage.setItem("coas_theme", t);
      setTheme(t);
    };
    window.addEventListener("coas-theme-changed", onThemeChanged);
    return () => window.removeEventListener("coas-theme-changed", onThemeChanged);
  }, []);
  // Backend'den temayı çek — localStorage sadece anlık görüntü için, asıl kaynak backend
  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL ?? ""}/api/admin/settings/theme`)
      .then((r) => r.json())
      .then((d) => {
        if (d.theme) {
          localStorage.setItem("coas_theme", d.theme);
          setTheme(d.theme);
        }
        if (d.cafe_name) {
          localStorage.setItem("coas_cafe_name", d.cafe_name);
        }
      })
      .catch(() => {});
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <AppShell />
              </ProtectedRoute>
            }
          />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
